// ===========================================
// HD PAGE HERO - Premium Full-Width Crystal Clear Design
// No Heavy Overlays - Pure HD Images with Smart Text Contrast
// ===========================================

import React from 'react';
import { ChevronRight, Sparkles, TrendingUp, TrendingDown } from 'lucide-react';

interface QuickStat {
  value: string | number;
  label: string;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
}

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
}

interface HDPageHeroProps {
  // Core content
  title: string;
  subtitle?: string;
  description?: string;
  
  // Visual elements
  backgroundImage?: string;
  backgroundOverlay?: 'dark' | 'purple' | 'blue' | 'gradient' | 'none';
  icon?: React.ReactNode;
  badge?: {
    text: string;
    variant?: 'purple' | 'green' | 'blue' | 'gold';
  };
  
  // Navigation
  breadcrumbs?: Array<{ label: string; href?: string; icon?: React.ReactNode }>;
  backButton?: { label: string; href: string };
  
  // Stats & Quick Info
  stats?: QuickStat[];
  quickFacts?: Array<{ icon: React.ReactNode; label: string; value: string }>;
  
  // Actions
  primaryAction?: QuickAction;
  secondaryAction?: QuickAction;
  actions?: React.ReactNode;
  
  // Tags/Badges
  tags?: Array<{ icon?: React.ReactNode; label: string; variant?: string }>;
  
  // Layout
  variant?: 'default' | 'compact' | 'full' | 'split';
  className?: string;
  children?: React.ReactNode;
}

// Premium HD Background Images - Crystal Clear 2400px, High Quality
export const heroBackgrounds = {
  // Dashboard - Professional business meeting
  dashboard: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=2400&q=95&auto=format&fit=crop',
  
  // Contacts - Diverse team collaboration
  contacts: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=2400&q=95&auto=format&fit=crop',
  
  // Campaigns - Modern marketing office
  campaigns: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=2400&q=95&auto=format&fit=crop',
  
  // Emails - Clean workspace with laptop
  emails: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=2400&q=95&auto=format&fit=crop',
  
  // Analytics - Data visualization dashboard
  analytics: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=2400&q=95&auto=format&fit=crop',
  
  // Settings - Modern tech workspace
  settings: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=2400&q=95&auto=format&fit=crop',
  
  // Travel - Beautiful Maldives destination
  travel: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=2400&q=95&auto=format&fit=crop',
  
  // Marketing - Creative team brainstorming
  marketing: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=2400&q=95&auto=format&fit=crop',
  
  // Leads - Sales team success
  leads: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=2400&q=95&auto=format&fit=crop',
  
  // Social Media - Social icons and digital marketing
  social: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=2400&q=95&auto=format&fit=crop',
  
  // SMS - Mobile communication
  sms: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=2400&q=95&auto=format&fit=crop',
  
  // WhatsApp - Travel communication and messaging
  whatsapp: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=2400&q=95&auto=format&fit=crop',
  
  // Landing Pages - Web design workspace
  landing: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=2400&q=95&auto=format&fit=crop',
  
  // AI - Futuristic technology
  ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=2400&q=95&auto=format&fit=crop',
  
  // Automation - Tech and workflows
  automation: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=2400&q=95&auto=format&fit=crop',
  
  // Bookings - Airplane travel
  bookings: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=2400&q=95&auto=format&fit=crop',
  
  // Invoices - Finance and accounting
  invoices: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=2400&q=95&auto=format&fit=crop',
  
  // Payments - Secure transactions
  payments: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=2400&q=95&auto=format&fit=crop',
};

const HDPageHero: React.FC<HDPageHeroProps> = ({
  title,
  subtitle,
  description,
  backgroundImage,
  backgroundOverlay = 'none',
  icon,
  badge,
  breadcrumbs,
  backButton,
  stats,
  quickFacts,
  primaryAction,
  secondaryAction,
  actions,
  tags,
  variant = 'default',
  className = '',
  children,
}) => {
  const badgeVariants = {
    purple: 'bg-violet-600 text-white shadow-lg',
    green: 'bg-emerald-600 text-white shadow-lg',
    blue: 'bg-blue-600 text-white shadow-lg',
    gold: 'bg-amber-500 text-white shadow-lg',
  };

  const heightClasses = {
    default: 'min-h-[300px]',
    compact: 'min-h-[220px]',
    full: 'min-h-[420px]',
    split: 'min-h-[360px]',
  };

  const overlayClasses = {
    dark: 'bg-black/60',
    purple: 'bg-gradient-to-t from-violet-900/70 via-purple-900/35 to-transparent',
    blue: 'bg-gradient-to-t from-slate-900/70 via-blue-900/35 to-transparent',
    gradient: 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
    none: '',
  };

  return (
    <section className={`relative w-full overflow-hidden ${heightClasses[variant]} ${className}`}>
      {/* Full-Width HD Background Image - Crystal Clear */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full">
          <img
            src={backgroundImage}
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 35%' }}
            loading="eager"
          />
        </div>
      )}

      {/* Subtle Gradient for Text Readability - Bottom focused */}
      {backgroundOverlay !== 'none' && (
        <div className={`absolute inset-0 ${overlayClasses[backgroundOverlay]}`} />
      )}

      {/* Content Container - Full Width */}
      <div className="relative z-10 h-full w-full">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8 h-full flex flex-col">
          {/* Top Row: Back Button / Breadcrumbs */}
          {(backButton || breadcrumbs) && (
            <div className="mb-4">
              {backButton && (
                <a
                  href={backButton.href}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-800 hover:bg-gray-50 transition-all text-sm font-semibold shadow-xl"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  {backButton.label}
                </a>
              )}
              {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="flex items-center gap-2 text-sm">
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <ChevronRight className="w-4 h-4 text-white/70" />}
                      {crumb.href ? (
                        <a
                          href={crumb.href}
                          className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors font-medium drop-shadow-lg"
                        >
                          {crumb.icon}
                          {crumb.label}
                        </a>
                      ) : (
                        <span className="flex items-center gap-1.5 text-white font-bold drop-shadow-lg">
                          {crumb.icon}
                          {crumb.label}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              )}
            </div>
          )}

          {/* Main Content - Bottom Aligned */}
          <div className="flex-1 flex flex-col justify-end pb-2">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              {/* Left: Title & Description */}
              <div className="flex-1">
                {badge && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${badgeVariants[badge.variant || 'purple']}`}>
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-bold">{badge.text}</span>
                  </div>
                )}

                <div className="flex items-end gap-5">
                  {icon && (
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-white shadow-2xl flex items-center justify-center text-violet-600">
                      {icon}
                    </div>
                  )}
                  <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight drop-shadow-2xl">
                      {title}
                    </h1>
                    {subtitle && (
                      <p className="text-xl md:text-2xl text-white mt-2 font-semibold drop-shadow-xl">
                        {subtitle}
                      </p>
                    )}
                    {description && (
                      <p className="text-white/90 mt-2 max-w-2xl text-lg drop-shadow-lg">
                        {description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {tags && tags.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-5">
                    {tags.map((tag, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-800 text-sm font-semibold shadow-xl"
                      >
                        {tag.icon}
                        {tag.label}
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                {(primaryAction || secondaryAction || actions) && (
                  <div className="flex flex-wrap items-center gap-4 mt-6">
                    {primaryAction && (
                      <button
                        onClick={primaryAction.onClick}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700 transition-all shadow-2xl hover:shadow-violet-500/25 hover:-translate-y-0.5"
                      >
                        {primaryAction.icon}
                        {primaryAction.label}
                      </button>
                    )}
                    {secondaryAction && (
                      <button
                        onClick={secondaryAction.onClick}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-800 font-bold hover:bg-gray-50 transition-all shadow-xl"
                      >
                        {secondaryAction.icon}
                        {secondaryAction.label}
                      </button>
                    )}
                    {actions}
                  </div>
                )}
              </div>

              {/* Right: Quick Facts Card */}
              {quickFacts && quickFacts.length > 0 && (
                <div className="lg:w-80 bg-white rounded-2xl p-6 shadow-2xl">
                  <h3 className="text-gray-900 font-bold text-lg mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                    Quick Information
                  </h3>
                  <div className="space-y-4">
                    {quickFacts.map((fact, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 flex-shrink-0">
                          {fact.icon}
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm">{fact.label}</p>
                          <p className="text-gray-900 font-semibold">{fact.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Row */}
          {stats && stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-5 shadow-xl hover:shadow-2xl transition-shadow"
                >
                  <div className="flex items-center justify-between mb-2">
                    {stat.icon && (
                      <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600">
                        {stat.icon}
                      </div>
                    )}
                    {stat.change && (
                      <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${
                        stat.trend === 'up'
                          ? 'bg-emerald-100 text-emerald-700'
                          : stat.trend === 'down'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {stat.trend === 'up' && <TrendingUp className="w-3 h-3" />}
                        {stat.trend === 'down' && <TrendingDown className="w-3 h-3" />}
                        {stat.change}
                      </div>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Custom Children */}
          {children}
        </div>
      </div>
    </section>
  );
};

export default HDPageHero;
